import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark';
const KEY = 'blino:theme';

const media = typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function stored(): ThemeChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function resolve(): ThemeChoice {
  return stored() ?? (media?.matches ? 'dark' : 'light');
}

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function apply(theme: ThemeChoice) {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#081624' : '#f2f8f7');
}

/** Call once before the first render so there is no light flash for dark-mode users. */
export function initTheme() {
  apply(resolve());
  // Follow the OS only while the person hasn't picked a theme themselves.
  media?.addEventListener('change', () => {
    if (!stored()) {
      apply(resolve());
      emit();
    }
  });
}

export function setTheme(theme: ThemeChoice) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* private mode: the choice just won't persist */
  }
  apply(theme);
  emit();
}

export function useTheme(): [ThemeChoice, (t: ThemeChoice) => void] {
  const theme = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    resolve,
    () => 'light' as ThemeChoice,
  );
  return [theme, setTheme];
}
